import { getPublicCurrencyV2Rpc, getPublicFeatureFlagsV2Rpc, getPublicProductGroupsRpc, getPublicProductTierOptionsV2Rpc } from 'pricing-pages-ui-public-app/__generated__/chirp/com/hubspot/buying/rpc/PublicProductRpc';
import { getBooleanFromString, getQueryParam, getCorrectedPrefetchCurrency, getShortLinkForRedirect, guessPrefetchCurrency, isPrefetchCurrencyResponse } from './earlyRequesterUtils';
import { getIsQA } from '../utils/envUtils';
import { getUtk } from '../utils/getUtk';
import quickFetch from 'quick-fetch';
import { makeChirpEarlyRequest } from 'rpc-client-utils';
import { COOKIE_ECHO_USER } from '../constants/EarlyRequesterNames';
import { isCountryCodeString } from '../utils/currencyTypes';
import { handleEarlyRequestError, normaliseCurrencyRequest } from '../utils/earlyRequest';
import { getBrowserLocale, getCountryCode } from '../utils/getPreferredLanguage';
import { getProductGroupBySlug } from '../utils/getProductGroupBySlug';
import { setHubspotUtk } from '../utils/windowGlobals';
import { getEffectiveManualLang } from 'pricing-pages-ui-components/utils/localeUtils';
(function (_URLSearchParams$get$, _URLSearchParams$get) {
  // getBrowserLocale reads the debug override (PUBLIC_DEBUG_BROWSER_LOCALE),
  // falls back to navigator language, always lowercased so the hash matches
  // what DFC queries compute when reading the cached result.
  const browserLocale = getBrowserLocale();
  const isQA = getIsQA();
  const utk = getUtk() || '';
  setHubspotUtk(utk); // global var so we can access in the app code

  const cookieUrl = isQA ? /* eslint-disable-next-line hubspot-dev/prefer-hubspot-url-utils */
  'https://api.hubspotqa.com/cookie-echo/v1/user' : quickFetch.getApiUrl('/cookie-echo/v1/user', false);
  // Redirect detection
  quickFetch.makeEarlyRequest(COOKIE_ECHO_USER, {
    url: cookieUrl,
    type: 'GET',
    dataType: 'json',
    // Relatively quick timeout since missing the redirect is not a big deal, and we don't want to block pageload long
    // This might mean that redirects skew towards NorthAm and those with fast internet connections
    timeout: 2000,
    whenFinished(response) {
      // This endpoint will 401 if you're not logged in so wont hit this block
      const email = response.email;

      // by default we don't redirect hubspot users. We can override that by having hsRedirect=true in query param
      const hsRedirect = getQueryParam('hsRedirect');
      const shouldRedirectHubSpotUsers = getBooleanFromString(hsRedirect || '');
      if (email.indexOf('@hubspot.com') > -1 && !shouldRedirectHubSpotUsers) {
        // Don't redirect hubspot users. Important for sales
        // who want a consistent demo experience on hubspot.com
        return;
      }

      // Use redirect so we don't keep the public page in history
      const redirectUrl = getShortLinkForRedirect(window.location.href);
      window.location.replace(redirectUrl);
    }
  });
  const preferredCountryCode = getCountryCode();
  const normalizedCountryCode = isCountryCodeString(preferredCountryCode) ? preferredCountryCode : undefined;
  void makeChirpEarlyRequest(Object.assign({}, getPublicFeatureFlagsV2Rpc, {
    earlyRequest: true
  }), {
    request: {
      utk,
      browserLocale,
      // Normalise to match what fetchPublicFeatureFlags sends
      preferredCountryCode: normalizedCountryCode
    }
  }).catch(handleEarlyRequestError);

  // Currency endpoint — normalise inputs before CHIRP call so the hash computed
  // here matches the hash the DFC query computes when reading the cached result.
  const currencyEarlyRequest = makeChirpEarlyRequest(Object.assign({}, getPublicCurrencyV2Rpc, {
    earlyRequest: true
  }), {
    request: normaliseCurrencyRequest({
      browserLocale,
      preferredCountryCode
    })
  }).catch(handleEarlyRequestError);

  /* Product groups and HSOC tier options need a currency before
     getPublicCurrencyV2 resolves. Use the URL currencyCode param when present
     (shared links, the currency picker); otherwise guess from the explicit
     country, then the site language. The guess misses whenever the visitor's
     IP country doesn't match the site language (a US visitor on hubspot.fr
     resolves USD, not EUR), so once the currency response arrives, re-fire in
     the resolved currency instead of leaving the app to refetch after it
     mounts. Early requests are keyed by request hash, so the app's query picks
     up whichever one matches the currency CurrencyContext displays. */
  const pageSlug = window.location.pathname.split('/')[2] || '';
  const productGroup = getProductGroupBySlug(pageSlug);
  /* Uppercased to match CurrencyContext, which reads the param the same way,
     so a hand-typed ?currencyCode=eur prefetches in EUR and hits the cache. */
  const urlCurrency = (_URLSearchParams$get$ = (_URLSearchParams$get = new URLSearchParams(window.location.search).get('currencyCode')) === null || _URLSearchParams$get === void 0 ? void 0 : _URLSearchParams$get.toUpperCase()) !== null && _URLSearchParams$get$ !== void 0 ? _URLSearchParams$get$ : null;
  const currencyCode = guessPrefetchCurrency({
    urlCurrency,
    countryCode: normalizedCountryCode,
    siteLang: getEffectiveManualLang()
  });
  const prefetchProductGroups = prefetchCurrencyCode => {
    if (!productGroup) {
      return;
    }
    void makeChirpEarlyRequest(Object.assign({}, getPublicProductGroupsRpc, {
      earlyRequest: true
    }), {
      request: {
        productGroup,
        currencyCode: prefetchCurrencyCode,
        preferredCountry: normalizedCountryCode,
        browserLocale
      }
    }).catch(handleEarlyRequestError);
  };

  /* HSOC V2 public page — fired for every non-US visitor regardless of which
   * URL they enter on, since we can't gate on the HSOC flag here (the
   * feature-flag RPC hasn't resolved yet at early-request time). HSOC V2 is an
   * EMEA-only market, so skipping confirmed-US visitors avoids a wasted request
   * at zero latency cost. We skip only when the country is *explicitly* US
   * (overrideLocale param or debug override); visitors whose country is left to
   * the backend's IP geo still get the prefetch so an EMEA visitor is never
   * starved. Real page routing still gates on the hubspotoncreditsv2 flag;
   * this only affects prefetch-cache warmth. */
  const shouldPrefetchTierOptions = normalizedCountryCode !== 'US';
  const prefetchTierOptions = prefetchCurrencyCode => {
    void makeChirpEarlyRequest(Object.assign({}, getPublicProductTierOptionsV2Rpc, {
      earlyRequest: true
    }), {
      request: {
        browserLocale,
        preferredCountryCode: normalizedCountryCode,
        currencyCode: prefetchCurrencyCode
      }
    }).catch(handleEarlyRequestError);
  };
  prefetchProductGroups(currencyCode);
  if (shouldPrefetchTierOptions) {
    prefetchTierOptions(currencyCode);
  }
  void currencyEarlyRequest.then(currencyResponse => {
    if (!isPrefetchCurrencyResponse(currencyResponse)) {
      return;
    }
    const correctedCurrencyCode = getCorrectedPrefetchCurrency({
      urlCurrency,
      guessedCurrency: currencyCode,
      resolvedDefaultCurrency: currencyResponse.defaultCurrencyCode,
      availableCurrencyCodes: currencyResponse.availableCurrencyCodes
    });
    if (!correctedCurrencyCode) {
      return;
    }
    prefetchProductGroups(correctedCurrencyCode);
    /* The backend has now resolved the country, so skip the HSOC re-fire for
       US visitors, who can't land on the EMEA-only HSOC page. */
    if (shouldPrefetchTierOptions && currencyResponse.countryCode !== 'US') {
      prefetchTierOptions(correctedCurrencyCode);
    }
  });
})();